import { SyntheticEvent } from "react";
import { Navigate, RouteObject, useNavigate, useParams } from "react-router-dom";
import { TabContext, TabList } from "@mui/lab";
import { Box, Stack, styled, Tab, tabsClasses, Typography as T } from "@mui/material";
import { AuditLog } from "datasets/components/AuditLog";
import useDatasetAssistant from "datasets/components/assistant/hooks/useDatasetAssistant";
import { useAuditLogEnabled } from "datasets/hooks/useAuditLogEnabled";
import AiAgentsSettings from "datasets/pages/settings/pages/AiAgentsSettings";
import Configurator from "datasets/pages/settings/pages/Configurator";
import GeneralSettings from "datasets/pages/settings/pages/GeneralSettings";
import UserAccess from "datasets/pages/settings/pages/UserAccess";
import yn from "yn";
import { HEADER_HEIGHT } from "~/components/AppHeader";
import { ProtectedAppRoute } from "~/components/ProtectedAppRoute";
import { ROUTE_TABS_HEIGHT } from "~/components/RouteTabs";
import { useSafeRoutes } from "~/hooks/useSafeRoutes";
import useUserPermission from "~/hooks/useUserPermission";
import { trackEvent } from "~/utils/monitoring";

const { VITE_COHORT_EXPLORER_ENABLED, VITE_DATASET_ACCESS_ENABLED } = import.meta.env;

const StyledTabList = styled(TabList)({
  borderBottom: "none",
  minWidth: 131,
  height: "fit-content",
  [`& .${tabsClasses.list}`]: {
    flexDirection: "column",
    gap: 8,
  },
  [`& .${tabsClasses.indicator}`]: {
    display: "none",
  },
});

const StyledTab = styled(Tab)(({ theme }) => ({
  alignItems: "start",
  borderRadius: "4px",
  color: theme.palette.grey[600],
  fontWeight: 500,
  padding: "6px 12px",
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
    color: "rgba(0, 0, 0, 0.87)",
  },
  "&[aria-selected=true]": {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.grey[200],
  },
}));

const routeDefinitions: RouteObject[] = [
  { index: true, element: <Navigate to="general" replace /> },
  { path: "general", element: <GeneralSettings /> },
  { path: "access", element: <UserAccess /> },
  { path: "audit-log", element: <AuditLog /> },
  { path: "ai-agents", element: <AiAgentsSettings /> },
  {
    path: "cohort/*",
    element: (
      <ProtectedAppRoute permission="view:cohort-explorer-config-app">
        <Configurator />
      </ProtectedAppRoute>
    ),
  },
];

const DatasetSettings = () => {
  const navigate = useNavigate();
  const { "*": subRoute } = useParams();
  const routes = useSafeRoutes(routeDefinitions);
  const hasConfiguratorPermission = useUserPermission("view:cohort-explorer-config-app");

  const showAssistant = useDatasetAssistant();
  const configuratorEnabled = yn(VITE_COHORT_EXPLORER_ENABLED) && hasConfiguratorPermission;
  const userAccessEnabled = yn(VITE_DATASET_ACCESS_ENABLED);
  const auditLogEnabled = useAuditLogEnabled();
  const currentTab = subRoute?.split("/")[0] || "general";
  const showTabList = configuratorEnabled || userAccessEnabled || auditLogEnabled || showAssistant;

  const handleTabChange = (_: SyntheticEvent, selectedTab: string) => {
    navigate(selectedTab);
    trackEvent("dataset_settings_tab_changed", {
      tab: selectedTab,
      previous_tab: currentTab,
    });
  };

  // Fixed height prevents layout shift when switching tabs with different content heights
  const settingsHeight = `calc(100vh - ${HEADER_HEIGHT} - ${ROUTE_TABS_HEIGHT}px)`;

  // Layout constants for centered container with edge-aligned scrollbar
  const maxContentWidth = 1200;
  const basePadding = 24;
  const sidebarGap = 32;
  const sidebarMinWidth = 131;
  const centeringMargin = `max(0px, calc((100% - ${maxContentWidth}px) / 2))`;
  const contentMaxWidth = maxContentWidth - sidebarMinWidth - sidebarGap;

  return (
    <Box
      sx={{
        display: "flex",
        height: settingsHeight,
        overflow: "hidden",
      }}
    >
      <TabContext value={currentTab}>
        {showTabList && (
          <Stack
            gap={1}
            sx={{
              ml: `calc(${centeringMargin} + ${basePadding}px)`,
              pt: 3,
              pr: `${sidebarGap}px`,
              flexShrink: 0,
            }}
          >
            <Box py={1}>
              <T component="h2" sx={{ fontSize: 14, fontWeight: 600 }}>
                Settings
              </T>
            </Box>
            <StyledTabList onChange={handleTabChange}>
              <StyledTab label="General" value="general" />
              {userAccessEnabled && <StyledTab label="User Access" value="access" />}
              {auditLogEnabled && <StyledTab label="Changelog" value="audit-log" />}
              {showAssistant && <StyledTab label="Analysis Agent" value="ai-agents" />}
              {configuratorEnabled && <StyledTab label="Cohort Explorer" value="cohort" />}
            </StyledTabList>
          </Stack>
        )}
        <Box flex={1} sx={{ overflowY: "auto" }}>
          <Box
            sx={{
              py: 3,
              px: `${basePadding}px`,
              ml: showTabList ? 0 : centeringMargin,
              maxWidth: showTabList ? contentMaxWidth : maxContentWidth,
              width: "100%",
            }}
          >
            {routes}
          </Box>
        </Box>
      </TabContext>
    </Box>
  );
};

export default DatasetSettings;
